/**
 * Attendee Avatar (piece 4, decision 0055): a circular initials badge taking
 * the person and the attending status only. The fill is PersonRecord's
 * avatarColor, given and never derived; row spacing is the token ratio 2.4×
 * the radius centre to centre, so circles never overlap; the RSVP badge sits at
 * 42% of the radius, offset to 78% of it.
 */
export const AVATAR_ROW_SPACING_RATIO = 2.4;
export const AVATAR_BADGE_RATIO = 0.42;
export const AVATAR_BADGE_OFFSET_RATIO = 0.78;

export type Attending = 'NeedsAction' | 'Accepted' | 'Declined' | 'Tentative';

export interface AvatarPerson {
  displayName: string;
  /** PersonRecord.avatarColor, a colour token name; never recomputed here. */
  avatarColor: string;
}

/** Two letters: the first of the first and last words, or the first two of one word. */
export function initialsOf(displayName: string): string {
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]!.charAt(0) + words[words.length - 1]!.charAt(0)).toUpperCase();
}

/** Centre-to-centre spacing from the radius, never a pixel floor. */
export function avatarSpacing(radius: number, ratio = AVATAR_ROW_SPACING_RATIO): number {
  return radius * ratio;
}

/** Circles overlap only below 2.0 radii; at 2.4 the edges sit 0.4 radii apart. */
export function avatarsOverlap(radius: number, ratio = AVATAR_ROW_SPACING_RATIO): boolean {
  return avatarSpacing(radius, ratio) < radius * 2;
}

export function edgeGap(radius: number, ratio = AVATAR_ROW_SPACING_RATIO): number {
  return avatarSpacing(radius, ratio) - radius * 2;
}

export function showsRsvpBadge(attending: Attending): boolean {
  return attending === 'Accepted';
}
